import React, { useState } from "react";
import { Card, Tag, Space, Flex } from "antd";
import { StarFilled } from "@ant-design/icons";

import { useTranslation } from "react-i18next";

import { API_URL, itemName, getCredentials, ORDER_TYPE_COLORS } from "../../utils/Util";
import { ItemAttributes } from "../../../types/model";
import { useAntdTheme, useNotification } from "../../hooks";

const LARGE_FONT_SIZE = 38;
const SMALL_FONT_SIZE = 25;

interface WorkstationItem {
  item_attributes: ItemAttributes;
  count: number;
}

interface ItemCardProps {
  item: WorkstationItem;
  priority?: number | string;
  showBigFont?: boolean;
  restaurantId: string;
  workstationID: string;
}

const ItemCard: React.FC<ItemCardProps> = ({
  item,
  priority,
  showBigFont,
  restaurantId,
  workstationID,
}) => {
  const [isCompleting, setIsCompleting] = useState(false);
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { showError } = useNotification();

  const attributes = item.item_attributes;

  const handleComplete = async () => {
    if (isCompleting) return;

    setIsCompleting(true);

    try {
      const response = await fetch(
        `${API_URL}/v1/r/${restaurantId}/workstations/${workstationID}/complete_tasks`,
        {
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${getCredentials()}`,
          },
          body: JSON.stringify({
            item_attributes_id: attributes.id,
            count: item.count,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to complete task");
      }
    } catch (error) {
      showError(t("error_occurred"));
    } finally {
      setIsCompleting(false);
    }
  };

  const baseFontSize = showBigFont ? LARGE_FONT_SIZE : SMALL_FONT_SIZE;

  // --- Logic: Border Colors & Priority Badges ---
  let borderColor = token.colorPrimary;
  let borderWidth = 2;
  let priorityBadge = null;

  const priorityGoldColor = "#ffc300";

  // Badge Styles: We set width to 100% to fill the 'Left Column' stack
  const badgeStyle: React.CSSProperties = {
    margin: 0,
    padding: "4px 0",
    width: "100%",
    textAlign: "center",
    fontSize: baseFontSize * 0.5,
    fontWeight: "bold",
    border: "none",
  };

  if (priority === "10000" || priority === 10000) {
    borderColor = priorityGoldColor;
    borderWidth = 4;
    priorityBadge = <Tag color="gold" icon={<StarFilled />} style={badgeStyle} />;
  } else if (priority === "3000" || priority === 3000) {
    borderColor = ORDER_TYPE_COLORS["to go"].color;
    borderWidth = 3;
    priorityBadge = (
      <Tag
        style={{
          ...badgeStyle,
          backgroundColor: ORDER_TYPE_COLORS["to go"].color,
          color: "#fff",
        }}
      >
        TO GO
      </Tag>
    );
  } else if (priority === "5000" || priority === 5000) {
    borderColor = ORDER_TYPE_COLORS["here"].color;
    borderWidth = 3;
    priorityBadge = (
      <Tag
        style={{
          ...badgeStyle,
          backgroundColor: ORDER_TYPE_COLORS["here"].color,
          color: "#fff",
        }}
      >
        HERE
      </Tag>
    );
  }

  // --- Logic: Quantity Display (Pre-Fix) ---
  const isMultiple = item.count > 1;

  // Visual Logic: "Alert" Orange if > 1, subtle if 1
  const qtyBadgeBg = isMultiple ? token.colorPrimary : "transparent";
  const qtyBadgeColor = isMultiple ? "#fff" : token.colorTextQuaternary;
  const qtyBadgeBorder = isMultiple ? "none" : `2px solid ${token.colorBorder}`;

  return (
    <Card
      hoverable
      onClick={handleComplete}
      style={{
        borderColor: borderColor,
        borderWidth: borderWidth,
        height: "100%",
        minHeight: 140, // Increased slightly to accommodate the stack
        cursor: "pointer",
        transition: "all 0.2s ease",
        opacity: isCompleting ? 0.6 : 1,
        pointerEvents: isCompleting ? "none" : "auto",
      }}
      styles={{
        body: {
          padding: 10,
          height: "100%",
        },
      }}
    >
      <Flex vertical style={{ height: "100%" }} gap={8}>
        {/* === TOP SECTION: Left (Meta) vs Right (Name) === */}
        <Flex align="start" gap={10} style={{ flex: 1 }}>
          {/* SECTION 1: TOP LEFT (Quantity Stacked on Badge) */}
          <Flex
            vertical
            align="center"
            gap={6}
            style={{
              flexShrink: 0,
              width: showBigFont ? "64px" : "52px", // Fixed width for alignment
              marginRight: "8px",
            }}
          >
            {/* Quantity Badge */}
            <div
              style={{
                backgroundColor: qtyBadgeBg,
                color: qtyBadgeColor,
                border: qtyBadgeBorder,
                borderRadius: token.borderRadius,
                width: "100%", // Fill the column
                aspectRatio: "1/1", // Keep it square
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: baseFontSize,
                fontWeight: 900,
              }}
            >
              {item.count}
            </div>

            {/* Priority/Type Badge */}
            {priorityBadge}
          </Flex>

          {/* SECTION 2: TOP RIGHT (Item Name & Modifiers) */}
          <Flex vertical style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: token.fontWeightStrong,
                fontSize: baseFontSize,
                lineHeight: 1.1,
                marginBottom: 4,
                wordBreak: "break-word",
              }}
            >
              {itemName(attributes)}
            </div>

            {/* Modifiers live here under the name */}
            {attributes.modifications && attributes.modifications.length > 0 && (
              <div
                style={{
                  color: token.colorTextSecondary,
                  fontSize: baseFontSize * 0.65,
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {attributes.modifications.join(", ")}
              </div>
            )}
          </Flex>
        </Flex>

        {/* === SECTION 3: BOTTOM (Notes - Full Width) === */}
        {attributes.notes && (
          <div
            style={{
              marginTop: "auto", // Pushes to bottom if card stretches
              paddingTop: 6,
              borderTop: `1px dashed ${token.colorBorder}`,
              fontSize: baseFontSize * 0.65,
            }}
          >
            {attributes.notes}
          </div>
        )}
      </Flex>
    </Card>
  );
};

export default ItemCard;
